import { useState } from 'react';
import { inputClass } from '@/components/ui';
import { cn } from '@/lib/utils';

// A "pick an existing option OR add a new one" control.
// - Renders a native <select> populated with `options`.
// - A trailing "+ Add new…" option reveals a free-text input.
// - Whatever name is active (selected or typed) is reported via onChange.
//
// Used for Designer/Brand and Store fields.

const ADD_NEW = '__add_new__';

export function ComboSelect({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  addLabel = '+ Add new…',
  addPlaceholder = 'Type a new name…',
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  addLabel?: string;
  addPlaceholder?: string;
}) {
  // "adding" mode: the user explicitly picked "Add new…".
  const [addingManual, setAddingManual] = useState(false);

  // A non-empty value that isn't a known option is treated as a custom entry
  // (e.g. loaded asynchronously in edit mode, or a value preset by the parent).
  const valueIsKnown = value !== '' && options.includes(value);
  const adding = addingManual || (value !== '' && !valueIsKnown);

  const selectValue = adding ? ADD_NEW : valueIsKnown ? value : '';

  return (
    <div className="space-y-2">
      <select
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value;
          if (next === ADD_NEW) {
            setAddingManual(true);
            onChange('');
          } else {
            setAddingManual(false);
            onChange(next);
          }
        }}
        className={cn(inputClass, 'appearance-none')}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value={ADD_NEW}>{addLabel}</option>
      </select>
      {adding && (
        <input
          className={inputClass}
          value={value}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          placeholder={addPlaceholder}
        />
      )}
    </div>
  );
}
